import React, { useMemo, useState } from "react";
import { Stack, DefaultButton, Icon, mergeStyles } from "@fluentui/react";
import BaseTextField from "./BaseTextField";
import { styles } from "../ExperimentDetailsForm/experimentDetailsForm.styles";
import {
  getReadableTypeFromString,
  getValueFromString,
} from "../../../Shared/shared.utils";
import { injectWiggleAnimation } from "../../../Shared/shared.styles";

interface BaseKeyValueFieldProps {
  keyLabel?: string;
  valueLabel?: string;
  inferType?: boolean;
  onInsert: (key: string, value: string | boolean | number) => void;
  isRequired?: boolean;
  insertButtonText?: string;
  keySubtitle?: string;
  valueSubtitle?: string;
  disabled?: boolean;
  errorMessage?: string[];
  validateKey?: (key: string) => string[];
  validateValue?: (value: string) => string[];
}

const BaseKeyValueField: React.FC<BaseKeyValueFieldProps> = ({
  keyLabel = "Name",
  valueLabel = "Value",
  inferType = false,
  onInsert,
  isRequired = false,
  insertButtonText = "Insert",
  keySubtitle,
  valueSubtitle,
  disabled = false,
  errorMessage,
  validateKey = () => [],
  validateValue = () => [],
}) => {
  const [saveWiggle, setSaveWiggle] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [valueInput, setValueInput] = useState("");
  const [keyErrors, setKeyErrors] = useState<string[]>([]);
  const [valueErrors, setValueErrors] = useState<string[]>([]);
  const referredType = useMemo(
    () => (inferType ? getReadableTypeFromString(valueInput) : undefined),
    [inferType, valueInput],
  );
  const inferTypeDescription = inferType
    ? "Type will be inferred from input"
    : undefined;
  const errorMessageString =
    errorMessage && errorMessage.length > 0
      ? errorMessage.join("; ")
      : undefined;

  const validateForm = () => {
    const keyValidationErrors = validateKey(keyInput);
    const valueValidationErrors = validateValue(valueInput);
    setKeyErrors(keyValidationErrors);
    setValueErrors(valueValidationErrors);
    return (
      keyValidationErrors.length === 0 && valueValidationErrors.length === 0
    );
  };

  const handleInsert = () => {
    const isValid = validateForm();
    if (isValid) {
      if (inferType) {
        onInsert(keyInput, getValueFromString(valueInput));
      } else {
        onInsert(keyInput, valueInput);
      }
      setKeyInput("");
      setValueInput("");
    } else {
      setSaveWiggle(true);
      setTimeout(() => setSaveWiggle(false), 600);
    }
  };

  return (
    <Stack tokens={{ childrenGap: 5 }}>
      <Stack
        horizontal
        tokens={{ childrenGap: 8 }}
        className={styles.keyValueFieldRowStart}
      >
        <Stack className={styles.keyValueFieldRowInput}>
          <BaseTextField
            fieldKey="key"
            label={keyLabel}
            value={keyInput}
            isRequired={isRequired}
            isEdit={true}
            subtitle={keySubtitle}
            onChange={(_fieldKey, v) => {
              setKeyInput(v || "");
              const errors = validateKey(v || "");
              setKeyErrors(errors);
            }}
            placeholder={`Enter ${keyLabel.toLowerCase()}`}
            className={styles.keyValueFieldRowInput}
            errorMessage={keyErrors}
          />
        </Stack>
        <Stack className={styles.keyValueFieldRowInput}>
          <BaseTextField
            fieldKey="value"
            label={valueLabel}
            value={valueInput}
            isRequired={isRequired}
            isEdit={true}
            subtitle={valueSubtitle}
            onChange={(_fieldKey, v) => {
              setValueInput(v || "");
              const errors = validateValue(v || "");
              setValueErrors(errors);
            }}
            placeholder={`Enter ${valueLabel.toLowerCase()}`}
            className={styles.keyValueFieldRowInput}
            suffix={referredType}
            errorMessage={valueErrors}
          />
        </Stack>
        <DefaultButton
          text={insertButtonText}
          onClick={handleInsert}
          disabled={disabled || !keyInput || !valueInput}
          style={saveWiggle ? injectWiggleAnimation : undefined}
          className={styles.keyValueFieldInsertButton}
        />
      </Stack>
      {inferTypeDescription && (
        <span
          className={mergeStyles(
            styles.experimentDetailsDescription,
            styles.experimentDetailsLabel,
          )}
        >
          <Icon iconName="Info" />
          <em>{inferTypeDescription}</em>
        </span>
      )}
      {errorMessageString && (
        <span className={styles.experimentDetailsError}>
          {errorMessageString}
        </span>
      )}
    </Stack>
  );
};

export default BaseKeyValueField;
